import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { logError } from 'util/logging';

type State = { state: 'fetching' } | { state: 'unauthenticated' } | { state: 'authenticated'; accessToken: string };

// use this hook if you want to do authenticated api calls
// outside of <AuthenticatedRoutes />

export default function useAccessToken() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  const [accessToken, setToken] = useState<State>({ state: 'fetching' });
  useEffect(() => {
    (async () => {
      if (isLoading) {
        setToken({ state: 'fetching' });
        return;
      }
      if (!isAuthenticated) {
        setToken({ state: 'unauthenticated' });
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        setToken({ state: 'authenticated', accessToken: token });
      } catch (error) {
        // If we can't get a token (expired, network error, etc), treat as unauthenticated
        // to avoid sending expired/invalid tokens
        logError('Failed to get access token:', error);
        setToken({ state: 'unauthenticated' });
      }
    })();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return [accessToken];
}

export function getAccessTokenOrUndefined(accessToken: State) {
  return accessToken.state === 'authenticated' ? accessToken.accessToken : undefined;
}
